import * as React from 'react';
import Grid from '@mui/material/Grid';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';

import { useAccount } from '../../auth';
import { useCatalogTypes, useCatalogTags } from '../../data/catalog';
import { useAccountEntitlements, hasAccess } from '../../data/useEntitlements';
import { CatalogTag } from './CatalogTag';
import { CatalogCard } from './CatalogCard';

export const CatalogGrid = ({
  itemType,
  itemSelectText,
  onItemSelect,
}: any) => {
  const { currentAccount } = useAccount();
  const entitlementsQuery = useAccountEntitlements(currentAccount.id);
  const entitlements = entitlementsQuery.data?.getData() as any[] | undefined;
  const catalogTagsQuery = useCatalogTags({
    size: 1000,
  });
  const catalogQuery = useCatalogTypes(currentAccount.id, {
    size: 1000,
    filter: {
      entity_type: itemType,
    },
  });
  const [textFilter, setTextFilter] = React.useState<string | undefined>();
  const [tagFilter, setTagFilter] = React.useState<string[]>([]);

  if (catalogTagsQuery.error || catalogQuery.error) {
    return <Alert severity="error">Error fetching catalog</Alert>;
  }

  const toggleTag = (tagName: string) => {
    if (tagFilter.includes(tagName)) {
      setTagFilter(tagFilter.filter((x) => x !== tagName));
    } else {
      const newTagFilter = [...tagFilter];
      newTagFilter.push(tagName);
      setTagFilter(newTagFilter);
    }
  };

  const tags: React.ReactNode[] = [];
  if (catalogTagsQuery.data) {
    for (const tag of catalogTagsQuery.data.getData()) {
      tags.push(
        <CatalogTag
          title={tag.title}
          key={tag.name}
          onClick={() => toggleTag(tag.name)}
          highlighted={tagFilter.includes(tag.name)}
          style={{
            cursor: 'pointer',
            userSelect: 'none',
          }}
        />,
      );
    }

    tags.push(
      <CatalogTag
        title="Preview"
        key="preview"
        onClick={() => toggleTag('preview')}
        highlighted={tagFilter.includes('preview')}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          background: '#bddcf4',
          color: '#000',
        }}
      />,
    );
  }

  // Map entity type to entitlement prefix — only connections and processors
  // are entitlement-gated. Datastores and blobstores are always accessible.
  const entitlementPrefix =
    itemType === 'integration' ? 'connection' : itemType;
  const isEntitlementGated =
    itemType === 'integration' || itemType === 'processor';

  const cards: React.ReactNode[] = [];
  if (catalogQuery.data) {
    for (const catalogType of catalogQuery.data.getData()) {
      if (
        (textFilter &&
          textFilter.length > 0 &&
          !catalogType.title.toLowerCase().includes(textFilter.toLowerCase()) &&
          !catalogType.headline
            .toLowerCase()
            .includes(textFilter.toLowerCase())) ||
        (tagFilter.length > 0 &&
          !catalogType.tags.some((x: any) => tagFilter.includes(x.name)))
      ) {
        continue;
      }

      const isEntitled = isEntitlementGated
        ? hasAccess(entitlements, entitlementPrefix, catalogType.name)
        : true;

      cards.push(
        <Grid item xs={12} md={4} key={itemType + catalogType.name}>
          <CatalogCard
            catalogType={catalogType}
            itemSelectText={itemSelectText}
            onItemSelect={onItemSelect}
            tagFilter={tagFilter}
            disabled={!isEntitled}
          />
        </Grid>,
      );
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <Backdrop
        sx={{
          background: 'rgba(255, 255, 255, 0.5)',
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={catalogTagsQuery.isLoading || catalogQuery.isLoading}
      >
        <CircularProgress />
      </Backdrop>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} key="filter-text">
          <TextField
            placeholder="Search connectors by name"
            variant="standard"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTextFilter(e.target.value)
            }
            style={{
              width: '100%',
              margin: '1.5em 0',
            }}
          />
        </Grid>
        <Grid item xs={12} md={8} key="filter-tags">
          <div
            style={{
              fontFamily: 'Noto Sans, sans-serif',
              textAlign: 'right',
              margin: '1.5em 0',
            }}
          >
            {tags}
          </div>
        </Grid>
        {cards}
      </Grid>
    </div>
  );
};
